import React, { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import classnames from "classnames";
import { Table } from "@shared/components";
import { StationIssue } from "@shared/models";
import { stationIssuesTableProperties } from "@containers/Station/constants/stationIssuesTableProperties";
import { HospitalCardEmptyState } from "@containers/Dashboard/components/DashboardHospitalContainer/HospitalCardEmptyState";
import { actions as sharedActions } from "@shared/store";
import { OVERLAY_TYPES } from "@shared/constants";
import { ReactComponent as ArrowIcon } from "@assets/images/icons/general/arrow-right-black.svg";

import "./index.scss";

interface StationIssuesCardProps {
  title: string;
  className?: string;
  stationIssues: StationIssue[];
  emptyState?: string;
  onRowClick: (item: StationIssue) => void;
  onClickMarkAsRead: (issue: StationIssue) => void;
  hospitalId?: number;
}

const StationIssuesCard = (props: StationIssuesCardProps) => {
  const dispatch = useDispatch();

  const { title, className, stationIssues, emptyState, onRowClick, hospitalId, onClickMarkAsRead } = props;

  const isEmpty = useMemo(() => !stationIssues.length, [stationIssues]);

  const issues = useMemo(() => {
    const MAX_ISSUES = 1;
    return stationIssues.slice(0, MAX_ISSUES);
  }, [stationIssues]);

  const tableProperties = useMemo(() => {
    return stationIssuesTableProperties(onClickMarkAsRead, onRowClick);
  }, [onClickMarkAsRead, onRowClick]);

  const openStationIssuesListOverlay = useCallback(() => {
    dispatch(
      sharedActions.showOverlay({
        type: OVERLAY_TYPES.STATION_ISSUES_LIST,
        closeOnOverlayClick: true,
        props: {
          hospitalId,
          onRowClick,
        },
      }),
    );
  }, [dispatch, hospitalId, onRowClick]);

  return (
    <div className={classnames("sdh-issues-card", className)}>
      <div
        className={classnames("hospital-dashboard-card-title sdh-issues-card-title", {
          "hospital-dashboard-card-empty": isEmpty,
        })}
      >
        <div className="hospital-dashboard-card-title-content">{title}</div>
        <div className="show-all-issues" onClick={openStationIssuesListOverlay}>
          View All <ArrowIcon />
        </div>
      </div>
      <div className="hospital-dashboard-card-content">
        {!isEmpty ? (
          <Table<StationIssue> items={issues} properties={tableProperties} />
        ) : (
          <HospitalCardEmptyState title={emptyState} />
        )}
      </div>
    </div>
  );
};

export default StationIssuesCard;
